import React, { useState, useEffect, useCallback, useRef } from "react";
import FreadomFutureApi from "@s/api/schoolGuidedPath";
import wordgameApi from "@s/api/wordgame";
import pictionaryApi from "@s/api/pictionary";
import { withRouter } from "react-router";
import Select, { components } from "react-select";
import AsyncSelect from "react-select/async";
import { ThemeButton } from "@sc/custom.styled";
import {
  ActivityModalContainer,
  ActivityPopUpContainer,
} from "@sc/ActivityModal.styled";
import { DropDownTitle, DropDownContainer } from "./TaskEditor.styled";
import { connectStore } from "redux-box";
import { module as schoolGuidedPath } from "@st/schoolGuidedPath";
import swal from "sweetalert2";
import { PanelHeader } from "@sc/custom.styled";
import * as constant from "@u/constant";
import {
  READ_ALONG_STORY,
  CONTENT_SUB_TYPE_READ_AND_SPEAK,
  CONTENT_SUB_TYPE_READ_TO_ME,
} from "@u/constant";
import useDebouncing from "@u/useDebouncing";
import "./style_task.css";

const PAGE_SIZE = 20;

const taskOrderOptions = [
  { value: constant.PRE_LIVE_CLASS, label: "Pre-Live class" },
  { value: constant.POST_LIVE_CLASS, label: "Post-Live class" },
];

const taskTypeOptions = [
  { value: constant.NEWS_FREAD, label: "News" },
  { value: constant.ACTIVITY, label: "Activity" },
  { value: constant.STORY, label: "Story" },
  { value: READ_ALONG_STORY, label: "Read Along Story" },
  { value: constant.QUIZ, label: "Quiz" },
  { value: constant.WORD_GAME, label: "Word Game" },
  { value: constant.PICTIONARY, label: "Pictionary" },
];

const taskTagOptions = [
  { value: constant.GRAMMAR, label: "GRAMMAR" },
  { value: constant.READING, label: "READING" },
  { value: constant.LISTENING, label: "LISTENING" },
  { value: constant.SPELLING, label: "SPELLING" },
];

const taskLevelOptions = [
  { value: constant.EASY, label: "EASY" },
  { value: constant.MEDIUM, label: "MEDIUM" },
  { value: constant.HARD, label: "HARD" },
];

const gradeLevelOptions = [
  { value: "Level 1", label: "Level 1" },
  { value: "Level 2", label: "Level 2" },
  { value: "Level 3", label: "Level 3" },
  { value: "Level 4", label: "Level 4" },
  { value: "Level 5", label: "Level 5" },
  { value: "Level 6", label: "Level 6" },
  { value: "Level 7", label: "Level 7" },
  { value: "Level 8", label: "Level 8" },
  { value: "Level 9", label: "Level 9" },
  { value: "Level 10", label: "Level 10" },
  { value: "Level 11", label: "Level 11" },
  { value: "Level 16", label: "Level 16" },
];

/**
 * Custom MenuList for react-select that fires onLoadMore when the user
 * scrolls near the bottom of the dropdown list.
 */
const CustomMenuList = (props) => {
  return (
    <components.MenuList {...props}>
      {props.children}
      {props.selectProps.isLoadingMore && (
        <div style={{ textAlign: "center", padding: "8px", color: "#999", fontSize: 12 }}>
          Loading more…
        </div>
      )}
    </components.MenuList>
  );
};

// ---------------------------------------------------------------------------

const TaskEditor = (props) => {
  const [selectedTaskOrder, setSelectedTaskOrder] = useState(null);
  const [selectedTaskType, setSelectedTaskType] = useState(null);
  const [selectedLevelType, setSelectedLevelType] = useState(null);
  const [selectedTaskTag, setSelectedTaskTag] = useState(null);
  const [selectedTaskContent, setSelectedTaskContent] = useState(null);
  const [selectedTaskItems, setSelectedTaskItems] = useState([]);
  const [selectedSections, setSelectedSections] = useState([]);
  const [sections, setSections] = useState([]);
  const [unit_type, setUnitType] = useState(null);
  const [selectedGradeLevel, setSelectedGradeLevel] = useState(null);

  // --- Item-based (wordgame / pictionary) dropdown state ---
  const [itemOptions, setItemOptions] = useState([]);
  const [itemSearch, setItemSearch] = useState("");
  const [itemOffset, setItemOffset] = useState(0);
  const [itemHasNext, setItemHasNext] = useState(false);
  const [itemLoading, setItemLoading] = useState(false);
  const [itemLoadingMore, setItemLoadingMore] = useState(false);

  const debouncedItemSearch = useDebouncing(itemSearch, 400);

  // --- Content (story/news) dropdown state ---
  const [contentOptions, setContentOptions] = useState([]);
  const [contentSearch, setContentSearch] = useState("");
  const [contentLoading, setContentLoading] = useState(false);
  const debouncedContentSearch = useDebouncing(contentSearch, 400);

  // -----------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------

  const isItemBasedType = (typeValue) =>
    typeValue === constant.WORD_GAME || typeValue === constant.PICTIONARY;

  /**
   * Returns the grade UUID to pass to the wordgame/pictionary API.
   * Priority: URL param grade_id (the current grade context) which is already
   * a UUID, falling back to the grade level dropdown value if available.
   */
  const getGradeIdForItemSearch = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const gradeIdFromUrl = urlParams.get("grade_id");
    if (gradeIdFromUrl) return gradeIdFromUrl;
    // If no URL param, use whatever the user selected in the grade level dropdown.
    return selectedGradeLevel ? selectedGradeLevel.value : "";
  };

  /** Map a raw API result item to the react-select option shape. */
  const mapItemToOption = (item) => ({
    value: item.id,
    label: item.word_id ? item.word_id.word || "Unknown" : "Unknown",
  });

  // -----------------------------------------------------------
  // Fetch item options (fresh or append for infinite scroll)
  // -----------------------------------------------------------

  const fetchItemOptions = useCallback(
    async ({ search = "", offset = 0, append = false }) => {
      if (!selectedTaskType || !isItemBasedType(selectedTaskType.value)) return;

      append ? setItemLoadingMore(true) : setItemLoading(true);

      try {
        const gradeId = getGradeIdForItemSearch();
        let qs = search ? `&search=${encodeURIComponent(search)}` : "";
        if (gradeId) qs += `&grade_id=${gradeId}`;

        let data;
        if (selectedTaskType.value === constant.WORD_GAME) {
          data = await wordgameApi.getWords(offset, qs);
        } else {
          data = await pictionaryApi.getImages(offset, qs);
        }

        const newOptions = (data.result || []).map(mapItemToOption);
        setItemOptions((prev) => (append ? [...prev, ...newOptions] : newOptions));
        setItemOffset(offset);
        setItemHasNext(!!data.has_next);
      } catch (err) {
        console.error("Error fetching item options", err);
      } finally {
        append ? setItemLoadingMore(false) : setItemLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedTaskType, selectedGradeLevel]
  );

  // Trigger fresh fetch whenever debounced search or task type changes
  useEffect(() => {
    if (selectedTaskType && isItemBasedType(selectedTaskType.value) && selectedGradeLevel) {
      fetchItemOptions({ search: debouncedItemSearch, offset: 0, append: false });
    }
  }, [debouncedItemSearch, selectedTaskType, selectedGradeLevel, fetchItemOptions]);

  // Load next page (called by custom MenuList on scroll to bottom)
  const loadMoreItems = useCallback(() => {
    if (itemHasNext && !itemLoadingMore && !itemLoading) {
      const nextOffset = itemOffset + PAGE_SIZE;
      fetchItemOptions({
        search: debouncedItemSearch,
        offset: nextOffset,
        append: true,
      });
    }
  }, [itemHasNext, itemLoadingMore, itemLoading, itemOffset, debouncedItemSearch, fetchItemOptions]);

  // -----------------------------------------------------------
  // Form population helpers
  // -----------------------------------------------------------

  const setField = () => {
    const active_task = props.schoolGuidedPath.active_task;
    if (active_task) {
      setSelectedTaskOrder(
        taskOrderOptions.find((ele) => ele.value === active_task.position_type) || null
      );

      // Resolve the dropdown option: a story with content_sub_type "read_and_speak"
      // was saved as a Read Along Story — restore that sentinel value.
      let resolvedTaskTypeValue = active_task.content_type;
      if (
        active_task.content_type === constant.STORY &&
        active_task.content_sub_type === CONTENT_SUB_TYPE_READ_AND_SPEAK
      ) {
        resolvedTaskTypeValue = READ_ALONG_STORY;
      }
      setSelectedTaskType(
        taskTypeOptions.find((ele) => ele.value === resolvedTaskTypeValue) || null
      );

      let tagOption = [];
      active_task.tag.forEach((tagValue) => {
        tagOption = [
          ...tagOption,
          ...taskTagOptions.filter((ele) => ele.value === tagValue),
        ];
      });
      setSelectedTaskTag(tagOption);

      if (active_task.content_type === constant.WORD_GAME || active_task.content_type === constant.PICTIONARY) {
        setSelectedTaskItems(
          active_task.task_items && active_task.task_items.length > 0
            ? active_task.task_items.map((i) => {
                const actualId = i.item_ref_id || i.item_id || (i.data && i.data.id);
                let wordLabel = actualId || "Unknown";
                if (i.data && i.data.word_id && i.data.word_id.word) {
                  wordLabel = i.data.word_id.word;
                } else if (i.data && i.data.associated_word && i.data.associated_word.word) {
                  wordLabel = i.data.associated_word.word; // Support pictionary if applicable
                }
                return {
                  value: actualId,
                  label: wordLabel,
                };
              })
            : []
        );
      } else {
        setSelectedTaskContent({
          _source: {
            name: active_task.name,
            image: active_task.thumbnail_url,
            description: active_task.description,
          },
          _id: active_task.content_id,
        });
      }
    }
  };

  const setDefaultValue = () => {
    setSelectedTaskOrder(null);
    setSelectedTaskType(null);
    setSelectedTaskTag(null);
    setSelectedTaskContent(null);
    setSelectedLevelType(null);
    setSelectedTaskItems([]);
    setSections([]);
    setSelectedSections([]);
    setSelectedGradeLevel(null);
    setItemOptions([]);
    setItemSearch("");
    setItemOffset(0);
    setItemHasNext(false);
    setContentSearch("");
    setContentOptions([]);
  };

  // -----------------------------------------------------------
  // Effects
  // -----------------------------------------------------------

  useEffect(() => {
    if (props.schoolGuidedPath.active_task) {
      setField();
    } else {
      setDefaultValue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.schoolGuidedPath.active_task]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const unit_type = urlParams.get("unit_type");
    const id = urlParams.get("gpId");
    setUnitType(unit_type);
    if (unit_type === "custom") {
      props.schoolGuidedPath.fetchSectionsForCustomGp(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (props.schoolGuidedPath.sections) {
      fetchSections();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.schoolGuidedPath.sections]);

  // -----------------------------------------------------------
  // Validation
  // -----------------------------------------------------------

  const checkData = () => {
    if (!selectedTaskType) return false;
    if (isItemBasedType(selectedTaskType.value)) {
      return !!(
        selectedTaskOrder &&
        selectedTaskTag &&
        selectedTaskTag.length > 0 &&
        selectedTaskItems &&
        selectedTaskItems.length > 0
      );
    }
    return !!(
      selectedTaskOrder &&
      selectedTaskTag &&
      selectedTaskTag.length > 0 &&
      selectedTaskContent
    );
  };

  const buildContentSubType = (typeValue, selectedContent, activeTask) => {
    if (typeValue === READ_ALONG_STORY) return CONTENT_SUB_TYPE_READ_AND_SPEAK;
    if (typeValue === constant.STORY) {
      if (selectedContent && selectedContent._source) {
        console.log(selectedContent)
        if (selectedContent._source.current_story_type === "epub") {
          return CONTENT_SUB_TYPE_READ_TO_ME;
        }
        if (activeTask && activeTask.content_id === selectedContent._id) {
          if (activeTask.content_sub_type === CONTENT_SUB_TYPE_READ_TO_ME) {
            return CONTENT_SUB_TYPE_READ_TO_ME;
          }
        }
      }
      return null; // Explicitly nullify for non-epub stories
    }
    return undefined;
  };

  // -----------------------------------------------------------
  // Payload builders
  // -----------------------------------------------------------

  const buildTaskPayload = (base) => {
    const apiContentType =
      selectedTaskType.value === READ_ALONG_STORY ? constant.STORY : selectedTaskType.value;

    const taskObj = {
      ...base,
      content_type: apiContentType,
      position_type: selectedTaskOrder.value,
      tag: selectedTaskTag.map((t) => t.value),
      unit: props.match.params.id,
    };

    const contentSubType = buildContentSubType(
      selectedTaskType.value,
      selectedTaskContent,
      props.schoolGuidedPath.active_task
    );
    if (contentSubType !== undefined) taskObj["content_sub_type"] = contentSubType;

    if (isItemBasedType(selectedTaskType.value)) {
      taskObj["content_id"] = "d5ac3e25-3f25-4a71-8ddf-b4afba8cc072"; // dummy
      taskObj["name"] =
        selectedTaskType.value === constant.WORD_GAME
          ? constant.WORD_GAME_NAME
          : constant.PICTIONARY_NAME;
      taskObj["description"] = "Speaking Feature Task";
      taskObj["thumbnail_url"] =
        selectedTaskType.value === constant.WORD_GAME
          ? constant.WORD_GAME_THUMBNAIL
          : constant.PICTIONARY_THUMBNAIL;
      taskObj["task_items"] = selectedTaskItems.map((item, index) => ({
        item_ref_id: item.value,
        order: index + 1,
      }));
    } else {
      taskObj["content_id"] = selectedTaskContent._id;
      taskObj["name"] = selectedTaskContent._source.name;
      taskObj["description"] = selectedTaskContent._source.description;
      taskObj["thumbnail_url"] = selectedTaskContent._source.image;
    }

    return taskObj;
  };

  const createTask = () => {
    if (!checkData()) {
      swal(
        "Oops!",
        `You must select 'TASK ORDER', 'TASK TYPE', 'TASK TAG' & '${
          selectedTaskType && isItemBasedType(selectedTaskType.value)
            ? "WORDS / IMAGES"
            : "CONTENT"
        }' to create Task`,
        "error"
      );
      return;
    }
    const taskObj = buildTaskPayload({
      all_sections: selectedSections && selectedSections.map((s) => s.value),
    });
    props.schoolGuidedPath.createTask(taskObj);
  };

  const updateTask = () => {
    if (!checkData()) {
      swal(
        "Oops!",
        `You must select 'TASK ORDER', 'TASK TYPE', 'TASK TAG' & '${
          selectedTaskType && isItemBasedType(selectedTaskType.value)
            ? "WORDS / IMAGES"
            : "CONTENT"
        }' to update Task`,
        "error"
      );
      return;
    }
    const taskObj = buildTaskPayload({
      task_id: props.schoolGuidedPath.active_task.id,
    });

    if (isItemBasedType(selectedTaskType.value) && props.schoolGuidedPath.active_task) {
      const active_task = props.schoolGuidedPath.active_task;
      const originalItems = active_task.task_items || [];
      let isChanged = false;

      if (selectedTaskItems.length !== originalItems.length) {
        isChanged = true;
      } else {
        for (let i = 0; i < selectedTaskItems.length; i++) {
          const originalId = originalItems[i].item_ref_id || originalItems[i].item_id || (originalItems[i].data && originalItems[i].data.id);
          if (selectedTaskItems[i].value !== originalId) {
            isChanged = true;
            break;
          }
        }
      }

      if (!isChanged) {
        delete taskObj.task_items;
      }
    }

    props.schoolGuidedPath.updateTask(taskObj);
  };

  // -----------------------------------------------------------
  // Content (story / news / activity / quiz) Search
  // -----------------------------------------------------------

  const handleContentSearchInput = (inputValue) => {
    setContentSearch(inputValue);
  };

  const handleChange = (value) => {
    setSelectedTaskContent(value);
  };

  const fetchContentOptions = useCallback(async (searchValue) => {
    if (!selectedTaskType || !selectedTaskType.value || isItemBasedType(selectedTaskType.value)) {
      setContentOptions([]);
      return;
    }
    setContentLoading(true);
    try {
      let searchIndex = selectedTaskType.value;
      if (selectedTaskType.value === constant.NEWS_FREAD) {
        searchIndex = constant.NEWS_FREAD_GLOBAL_SEARCH;
      } else if (selectedTaskType.value === READ_ALONG_STORY) {
        searchIndex = constant.STORY;
      }

      const contentGradeLevel = selectedGradeLevel ? selectedGradeLevel.value : "";
      const data = await FreadomFutureApi.getContentSearchList(
        searchValue,
        searchIndex,
        contentGradeLevel
      );
      setContentOptions(data.documents || []);
    } catch (error) {
      console.error(error);
      setContentOptions([]);
    } finally {
      setContentLoading(false);
    }
  }, [selectedTaskType, selectedGradeLevel]);

  useEffect(() => {
    if (selectedTaskType && !isItemBasedType(selectedTaskType.value) && selectedGradeLevel) {
      fetchContentOptions(debouncedContentSearch);
    }
  }, [debouncedContentSearch, selectedTaskType, selectedGradeLevel, fetchContentOptions]);

  // -----------------------------------------------------------
  // Item-based dropdown handlers
  // -----------------------------------------------------------

  const handleTaskItemsChange = (items) => {
    setSelectedTaskItems(items || []);
  };

  const handleItemSearchInput = (inputValue, { action }) => {
    if (action === "input-change") {
      setItemSearch(inputValue);
    }
  };

  const handleTaskTypeChange = (value) => {
    setSelectedTaskContent(null);
    setSelectedLevelType(null);
    setSelectedTaskItems([]);
    setItemOptions([]);
    setItemSearch("");
    setItemOffset(0);
    setItemHasNext(false);
    setContentSearch("");
    setContentOptions([]);
    setSelectedTaskType(value);
  };

  // -----------------------------------------------------------
  // Sections
  // -----------------------------------------------------------

  const fetchSections = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const grade = urlParams.get("grade_id");
    const sectionsList = props.schoolGuidedPath.sections[grade];
    setSections(
      sectionsList
        ? sectionsList.map((section) => ({ value: section.id, label: section.name }))
        : []
    );
  };

  const handleSection = (section) => {
    setSelectedSections(section);
  };

  // Removed dynamic ItemMenuList in favor of native onMenuScrollToBottom

  // -----------------------------------------------------------
  // Render
  // -----------------------------------------------------------

  return (
    <ActivityPopUpContainer style={{ height: "100%" }}>
      <PanelHeader primary style={{ zIndex: "999" }}>
        <span>Create TASK</span>
        {props.schoolGuidedPath.active_task ? (
          <ThemeButton primary onClick={() => setField()}>
            Reset
          </ThemeButton>
        ) : null}
        {props.schoolGuidedPath.active_task ? (
          <ThemeButton primary onClick={() => updateTask()}>
            Done
          </ThemeButton>
        ) : (
          <ThemeButton primary onClick={() => createTask()}>
            Create
          </ThemeButton>
        )}
      </PanelHeader>
      <ActivityModalContainer className="mcq_body">
        {unit_type === "custom" && !props.schoolGuidedPath.active_task && (
          <Select
            isMulti={true}
            className="dropdown-container"
            onChange={handleSection}
            value={selectedSections}
            options={sections}
            placeholder="Select Section"
            isDisabled={!!props.schoolGuidedPath.active_task}
          />
        )}

        <DropDownContainer>
          <DropDownTitle>TASK ORDER</DropDownTitle>
          <Select
            placeholder="Select Task Order"
            value={selectedTaskOrder}
            onChange={setSelectedTaskOrder}
            options={taskOrderOptions}
          />
        </DropDownContainer>

        <DropDownContainer>
          <DropDownTitle>TASK TYPE</DropDownTitle>
          <Select
            placeholder="Select Task Type"
            value={selectedTaskType}
            onChange={handleTaskTypeChange}
            options={taskTypeOptions}
          />
        </DropDownContainer>

        <DropDownContainer>
          <DropDownTitle>TASK TAG</DropDownTitle>
          <Select
            placeholder="Select Task Tag"
            value={selectedTaskTag}
            onChange={setSelectedTaskTag}
            options={taskTagOptions}
            isMulti={true}
          />
        </DropDownContainer>

        <DropDownContainer>
          <DropDownTitle>GRADE LEVEL</DropDownTitle>
          <Select
            placeholder="Select Grade Level"
            value={selectedGradeLevel}
            onChange={setSelectedGradeLevel}
            options={gradeLevelOptions}
          />
        </DropDownContainer>

        {selectedTaskType && isItemBasedType(selectedTaskType.value) ? (
          <DropDownContainer>
            <DropDownTitle>
              SELECT {selectedTaskType.value === constant.WORD_GAME ? "WORDS" : "IMAGES"}
            </DropDownTitle>
            <Select
              isMulti
              isDisabled={!selectedTaskType || !selectedTaskType.value}
              value={selectedTaskItems}
              options={itemOptions}
              onChange={handleTaskItemsChange}
              onInputChange={handleItemSearchInput}
              isLoading={itemLoading}
              filterOption={() => true}
              components={{ MenuList: CustomMenuList }}
              onMenuScrollToBottom={loadMoreItems}
              isLoadingMore={itemLoadingMore}
              placeholder={itemLoading ? "Loading…" : "Search and select…"}
              noOptionsMessage={() =>
                itemLoading ? "Loading…" : "No results found"
              }
            />
          </DropDownContainer>
        ) : (
          <DropDownContainer>
            <DropDownTitle>CONTENT</DropDownTitle>
            <Select
              isDisabled={!selectedTaskType || !selectedTaskType.value}
              value={selectedTaskContent}
              key={selectedTaskType ? selectedTaskType.value : "no-type"}
              getOptionLabel={(e) => e._source.name}
              getOptionValue={(e) => e._id}
              options={contentOptions}
              onInputChange={handleContentSearchInput}
              onChange={handleChange}
              isLoading={contentLoading}
              placeholder={contentLoading ? "Loading…" : "Search content…"}
            />
          </DropDownContainer>
        )}
      </ActivityModalContainer>
    </ActivityPopUpContainer>
  );
};

export default withRouter(
  connectStore({
    schoolGuidedPath,
  })(TaskEditor)
);
